import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, ChevronDown, CheckCircle2 } from "lucide-react";
import { Reveal, SectionTitle } from "../components/common";
import { getIcon } from "../lib/icons";
import { publicApi } from "../lib/api";
import { LOCATIONS } from "../data/locations";
import GoogleReviewsSection from "../components/GoogleReviewsSection";
import Seo from "../components/Seo";

// Surfaces rotate as you scroll, so each group of services reads as its own
// chapter rather than one long alternating grid.
const TONES = [
  { key: "white", section: "bg-white", dark: false },
  { key: "navy", section: "bg-navy", dark: true },
  { key: "light", section: "bg-slate-50", dark: false },
];

// One service. The first in each group is given the wide treatment, which breaks
// the uniform grid and means a group of four or seven never ends in an orphan.
const ServiceCard = ({ service, dark, feature }) => {
  const Icon = getIcon(service.icon);
  return (
    <Link
      to={service.external_path || `/services/${service.slug}`}
      data-testid={`service-card-${service.slug}`}
      className={`group relative flex h-full flex-col overflow-hidden rounded-2xl p-7 md:p-8 border duration-300 hover:-translate-y-1.5 [transition-property:transform,box-shadow,border-color,background-color] ${
        feature ? "md:col-span-2" : ""
      } ${
        dark
          ? "border-white/10 bg-white/[0.04] backdrop-blur-xl hover:bg-white/[0.07] hover:border-white/20"
          : "border-slate-200/80 bg-white shadow-sm hover:shadow-xl hover:border-slate-300"
      }`}
    >
      {/* A light bleed that only arrives on hover, so depth is felt rather than seen. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-orange/10 opacity-0 blur-2xl duration-500 [transition-property:opacity] group-hover:opacity-100"
      />
      <div className={`relative flex h-full flex-col ${feature ? "md:flex-row md:items-center md:gap-10" : ""}`}>
        <div className={feature ? "md:w-[45%] md:shrink-0" : ""}>
          <span
            className={`inline-flex items-center justify-center rounded-xl duration-300 [transition-property:background-color,color] ${
              feature ? "h-16 w-16" : "h-14 w-14"
            } ${dark ? "bg-white/10 text-white group-hover:bg-orange" : "bg-navy/5 text-navy group-hover:bg-orange group-hover:text-white"}`}
          >
            <Icon size={feature ? 29 : 25} />
          </span>
          <h3
            className={`font-display font-bold mt-6 ${feature ? "text-xl md:text-2xl leading-tight" : "text-lg"} ${
              dark ? "text-white" : "text-navy"
            }`}
          >
            {service.title}
          </h3>
        </div>
        <div className={`flex flex-1 flex-col ${feature ? "mt-5 md:mt-0" : ""}`}>
          <p className={`text-sm leading-relaxed ${feature ? "md:text-base" : "mt-3"} ${dark ? "text-slate-300" : "text-slate-600"}`}>
            {service.short}
          </p>
          <span
            className={`mt-6 inline-flex items-center gap-1.5 text-sm font-bold duration-300 [transition-property:gap,color] group-hover:gap-3 ${
              feature ? "md:mt-6" : "mt-auto pt-3"
            } ${dark ? "text-orange" : "text-orange group-hover:text-orange-dark"}`}
          >
            Learn more <ArrowRight size={15} />
          </span>
        </div>
      </div>
    </Link>
  );
};

const CREDENTIALS = ["Xero Platinum Partner", "Tamworth & the Midlands", "Your outsourced finance team"];

export default function Services() {
  const [services, setServices] = useState([]);
  const [groups, setGroups] = useState([]);
  const [areasOpen, setAreasOpen] = useState(false);
  useEffect(() => {
    publicApi.services().then(setServices).catch(() => {});
    publicApi.serviceGroups().then(setGroups).catch(() => {});
  }, []);

  const sections = (() => {
    const ordered = [...groups].sort((a, b) => (a.order || 0) - (b.order || 0));
    const cols = ordered.map((g) => ({ ...g, items: services.filter((s) => s.group === g.key) }));
    const known = new Set(ordered.map((g) => g.key));
    const orphans = services.filter((s) => !known.has(s.group));
    if (orphans.length) cols.push({ key: "_other", label: "More services", items: orphans });
    return cols.filter((c) => c.items.length > 0);
  })();

  return (
    <div>
      <Seo title="Accounting, Outsourced Finance & Xero Services" description="From bookkeeping, payroll and VAT to outsourced finance, Virtual FD, management accounts, cash flow forecasting and business advisory — explore how CASS, a Xero Platinum Partner, supports growing businesses across Tamworth, Lichfield, Sutton Coldfield, Birmingham, Solihull and Burton upon Trent." />

      {/* Hero — the pattern band, with a light bleed layered over it and the
          credentials a visitor is actually weighing up. */}
      <section className="numbers-pattern relative overflow-hidden text-white" data-testid="services-hero">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(circle at 18% 28%, rgba(46,163,253,0.20), transparent 55%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="grid lg:grid-cols-[minmax(0,1fr),320px] gap-12 lg:gap-16 items-end">
            <div>
              <span className="text-orange font-bold text-sm tracking-[0.2em] uppercase">Our Services</span>
              <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] mt-4">
                How we<br />can help
              </h1>
              <p className="mt-6 text-slate-300 max-w-2xl leading-relaxed">
                Every business is different, which is why our services are tailored to your needs. From day-to-day bookkeeping, payroll
                and VAT through to outsourced finance support, Virtual FD, financial reports, cash flow forecasting and Strategic
                Review &amp; Insights — we act as your long-term finance partner, not just your year-end accountant.
              </p>
              <ul className="mt-8 flex flex-wrap gap-2.5">
                {CREDENTIALS.map((c) => (
                  <li
                    key={c}
                    data-testid={`services-credential-${c.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] backdrop-blur-xl px-4 py-2 text-xs font-semibold text-slate-200"
                  >
                    <CheckCircle2 size={13} className="text-orange" /> {c}
                  </li>
                ))}
              </ul>
            </div>

            {/* Jump straight to the part of the list you came for. */}
            {sections.length > 1 && (
              <nav aria-label="Service groups" data-testid="services-group-nav" className="lg:pb-2">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Jump to</p>
                <ul className="mt-4 space-y-1.5">
                  {sections.map((sec) => (
                    <li key={sec.key}>
                      <a
                        href={`#group-${sec.key}`}
                        data-testid={`services-jump-${sec.key}`}
                        className="group flex items-baseline justify-between gap-4 rounded-lg px-3 py-2 -mx-3 text-sm font-semibold text-slate-300 duration-200 [transition-property:color,background-color] hover:bg-white/[0.06] hover:text-white"
                      >
                        <span>{sec.label}</span>
                        <span className="text-xs font-bold text-slate-500 tabular-nums group-hover:text-orange">{sec.items.length}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </div>
        </div>
      </section>

      {sections.map((sec, idx) => {
        const tone = TONES[idx % TONES.length];
        const [flagship, ...rest] = sec.items;
        return (
          <section
            key={sec.key}
            id={`group-${sec.key}`}
            data-testid={`services-group-${sec.key}`}
            className={`scroll-mt-24 py-20 md:py-28 ${tone.section}`}
          >
            <div className="max-w-7xl mx-auto px-6">
              <Reveal>
                <div className="flex flex-wrap items-end justify-between gap-6 mb-12 md:mb-14">
                  <div>
                    <span className="text-orange font-bold text-xs tracking-[0.2em] uppercase">
                      {idx === 0 ? "Where most clients start" : "Services"}
                    </span>
                    <h2
                      className={`font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-3 ${
                        tone.dark ? "text-white" : "text-navy"
                      }`}
                    >
                      {sec.label}
                    </h2>
                  </div>
                  <p className={`text-sm font-semibold ${tone.dark ? "text-slate-400" : "text-slate-400"}`}>
                    {sec.items.length} service{sec.items.length === 1 ? "" : "s"}
                  </p>
                </div>
              </Reveal>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
                {flagship && (
                  <Reveal className="md:col-span-2">
                    <ServiceCard service={flagship} dark={tone.dark} feature />
                  </Reveal>
                )}
                {rest.map((s, i) => (
                  <Reveal key={s.slug} delay={(i + 1) * 60}>
                    <ServiceCard service={s} dark={tone.dark} />
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      <GoogleReviewsSection testId="services-reviews" />

      {/* Local areas — internal linking to location pages */}
      <section className="py-16 md:py-20 bg-white" data-testid="services-areas">
        <div className="max-w-6xl mx-auto px-6">
          <SectionTitle eyebrow="Local support" title="Supporting businesses across the Midlands" />
          <p className="text-slate-500 max-w-2xl -mt-4 mb-6">Wherever you are, you'll get the same proactive, Xero-focused support. Explore how we help businesses in your area.</p>

          <button
            type="button"
            onClick={() => setAreasOpen((v) => !v)}
            aria-expanded={areasOpen}
            aria-controls="services-areas-list"
            data-testid="services-areas-toggle"
            className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-5 py-2.5 text-sm font-semibold text-navy hover:border-orange duration-200 [transition-property:border-color,background-color]"
          >
            <MapPin size={15} className="text-orange" /> {areasOpen ? "Hide areas we support" : "Show areas we support"}
            <ChevronDown size={16} className={`duration-300 [transition-property:transform] ${areasOpen ? "rotate-180" : ""}`} />
          </button>
          <div
            id="services-areas-list"
            className={`overflow-hidden duration-300 ease-out [transition-property:max-height,opacity,margin] ${areasOpen ? "max-h-[1200px] opacity-100 mt-6" : "max-h-0 opacity-0 mt-0"}`}
            aria-hidden={!areasOpen}
          >
            <div className="flex flex-wrap gap-3">
              {LOCATIONS.map((l) => (
                <Link key={l.slug} to={`/${l.slug}`} data-testid={`services-area-${l.slug}`}
                  className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-5 py-2.5 text-sm font-semibold text-navy hover:border-orange hover:-translate-y-0.5 duration-200 [transition-property:transform,border-color]">
                  <MapPin size={14} className="text-orange" /> {l.town}
                </Link>
              ))}
            </div>
            <div className="mt-6">
              <Link to="/areas-we-support" data-testid="services-areas-hub" className="inline-flex items-center gap-2 text-orange font-bold hover:underline">See all areas we support <ArrowRight size={16} /></Link>
            </div>
          </div>
        </div>
      </section>


      {/* Closing CTA — left-aligned, with the light bleed used in the hero. */}
      <section className="relative overflow-hidden bg-navy text-white py-20 md:py-24" data-testid="services-cta">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(circle at 75% 35%, rgba(46,163,253,0.22), transparent 55%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight">Not sure what you need?</h2>
            <p className="text-slate-300 mt-4 leading-relaxed">
              Most people aren't, at this stage. Tell us what the business does and what's getting in the way, and we'll tell you
              straight which of these would actually help — or see an indicative price first, before you give us any details.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/contact"
                data-testid="services-cta-contact"
                className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full duration-300 [transition-property:transform,background-color] hover:-translate-y-0.5"
              >
                Let's talk <ArrowRight size={17} />
              </Link>
              <Link
                to="/pricing"
                data-testid="services-cta-pricing"
                className="inline-flex items-center gap-2 border border-white/25 hover:border-white/60 text-white font-bold px-8 py-4 rounded-full duration-300 [transition-property:border-color,background-color] hover:bg-white/[0.06]"
              >
                See what it would cost
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
